/**
 * Phase 1.7 — shared Add Animal dialog.
 *
 * Extracted from AnimalsPage v1 so AnimalsPageV2 can reuse the same form
 * without re-implementing it. Both render the same shadcn Dialog + form.
 *
 * On success, calls `onAdded()` so callers can refetch their list.
 *
 * Image attachment (added 2026-05): users can stage an image at create
 * time. After POST /api/animals returns the new row, the staged file is
 * uploaded as an attachment linked to the animal. Failure to upload the
 * image surfaces a warning but does not roll back the create — the
 * record is saved and the user can attach later from the detail page.
 */

import { useEffect, useState, type ReactNode } from 'react';
import { Plus } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ImageStager } from '@/components/ImageStager';
import { authFetch } from '@/lib/auth';
import { uploadAttachment } from '@/lib/attachments';
import { toast } from 'sonner';
import type { Pasture, Species } from '@shared/types';

export interface AddAnimalDialogProps {
  trigger?: ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  onAdded?: () => void;
}

const DEFAULT_TRIGGER = (
  <Button
    size="sm"
    className="bg-burnt-orange hover:bg-burnt-orange/90 text-white dark:bg-burnt-orange-dark dark:hover:bg-burnt-orange-dark/90"
  >
    <Plus className="h-4 w-4" />
    Add animal
  </Button>
);

export function AddAnimalDialog({
  trigger,
  open: controlledOpen,
  onOpenChange,
  onAdded,
}: AddAnimalDialogProps): JSX.Element {
  const [internalOpen, setInternalOpen] = useState<boolean>(false);
  const open = controlledOpen ?? internalOpen;
  const setOpen = (next: boolean): void => {
    setInternalOpen(next);
    onOpenChange?.(next);
  };
  const [pastures, setPastures] = useState<Pasture[]>([]);
  const [species, setSpecies] = useState<Species[]>([]);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [name, setName] = useState<string>('');
  // Holds the selected species_id (or '' when unset). Sent as null when
  // empty so the backend gets a clean column-null instead of an empty
  // string that would fail the composite FK.
  const [speciesId, setSpeciesId] = useState<string>('');
  const [breed, setBreed] = useState<string>('');
  const [tag, setTag] = useState<string>('');
  const [birth, setBirth] = useState<string>('');
  const [pasture, setPasture] = useState<string>('');
  const [notes, setNotes] = useState<string>('');
  const [imageFile, setImageFile] = useState<File | null>(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    Promise.all([
      authFetch<{ items: Pasture[] }>('/api/pastures'),
      authFetch<{ items: Species[] }>('/api/species'),
    ])
      .then(([pData, sData]) => {
        if (!cancelled) {
          setPastures(pData.items ?? []);
          setSpecies(sData.items ?? []);
        }
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [open]);

  const reset = (): void => {
    setName('');
    setSpeciesId('');
    setBreed('');
    setTag('');
    setBirth('');
    setPasture('');
    setNotes('');
    setImageFile(null);
  };

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error('Name is required');
      return;
    }
    setSubmitting(true);
    try {
      const created = await authFetch<{ id: string }>('/api/animals', {
        method: 'POST',
        body: JSON.stringify({
          name: name.trim(),
          // Real species_id from the dropdown — drops the PR #391 stopgap
          // that hard-coded null here.
          species_id: speciesId || null,
          breed: breed.trim() || null,
          tag_number: tag.trim() || null,
          dob: birth || null,
          pasture_id: pasture || null,
          status: 'active',
          notes: notes.trim() || null,
        }),
      });

      if (imageFile && created?.id) {
        try {
          await uploadAttachment(imageFile, {
            linked_entity_type: 'animal',
            linked_entity_id: created.id,
          });
        } catch (uploadErr) {
          toast.warning(
            `Saved, but image upload failed: ${(uploadErr as Error).message ?? 'unknown error'}`,
          );
        }
      }

      toast.success('Animal added');
      reset();
      setOpen(false);
      onAdded?.();
    } catch (err) {
      toast.error((err as Error).message ?? 'Failed to add');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger ?? DEFAULT_TRIGGER}</DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Add Animal</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-2">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Name *</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Bessie" />
            </div>
            <div className="space-y-2">
              <Label>Species</Label>
              <Select value={speciesId} onValueChange={setSpeciesId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select species..." />
                </SelectTrigger>
                <SelectContent>
                  {species.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Breed</Label>
              <Input value={breed} onChange={(e) => setBreed(e.target.value)} placeholder="Angus" />
            </div>
            <div className="space-y-2">
              <Label>Tag #</Label>
              <Input value={tag} onChange={(e) => setTag(e.target.value)} placeholder="A-001" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Birth Date</Label>
              <Input type="date" value={birth} onChange={(e) => setBirth(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Pasture</Label>
              <Select value={pasture} onValueChange={setPasture}>
                <SelectTrigger>
                  <SelectValue placeholder="Select..." />
                </SelectTrigger>
                <SelectContent>
                  {pastures.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-2">
            <Label>Notes</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} />
          </div>
          <div className="space-y-2">
            <Label>Image</Label>
            <ImageStager
              file={imageFile}
              onChange={setImageFile}
              disabled={submitting}
            />
          </div>
          <Button
            type="submit"
            disabled={submitting}
            className="w-full bg-burnt-orange hover:bg-burnt-orange/90 text-white dark:bg-burnt-orange-dark dark:hover:bg-burnt-orange-dark/90"
          >
            {submitting ? 'Adding...' : 'Add Animal'}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
